import type { SnackbarKey } from "notistack";
import { useCallback, useSyncExternalStore } from "react";
import type { ProgressUpdate, ToastMessage } from "./toast-options";

export type ProgressState = {
  message: ToastMessage;
  value?: number;
};

type Listener = () => void;

// Live state for progress toasts, keyed by toast key. Notistack cannot update a
// toast in place and toast content renders under the app-root provider, so the
// handle writes here and only the subscribed toast re-renders.
const states = new Map<SnackbarKey, ProgressState>();
const listeners = new Map<SnackbarKey, Set<Listener>>();

const notify = (key: SnackbarKey) => {
  listeners.get(key)?.forEach((listener) => listener());
};

export const progressStore = {
  set(key: SnackbarKey, state: ProgressState): void {
    states.set(key, state);
    notify(key);
  },
  update(key: SnackbarKey, patch: ProgressUpdate): void {
    const current = states.get(key);
    if (!current) return;
    states.set(key, {
      message: "message" in patch && patch.message !== undefined ? patch.message : current.message,
      value: "value" in patch ? patch.value : current.value,
    });
    notify(key);
  },
  get(key: SnackbarKey): ProgressState | undefined {
    return states.get(key);
  },
  remove(key: SnackbarKey): void {
    if (states.delete(key)) notify(key);
  },
  subscribe(key: SnackbarKey, listener: Listener): () => void {
    const set = listeners.get(key) ?? new Set<Listener>();
    listeners.set(key, set);
    set.add(listener);
    return () => {
      set.delete(listener);
      if (set.size === 0) listeners.delete(key);
    };
  },
  /**
   * Test seam. Forgets every toast and its subscribers without closing anything in
   * notistack, so a progress toast still showing loses its live updates for good.
   * Production code removes per key from `onExited`.
   */
  clear(): void {
    states.clear();
    listeners.clear();
  },
};

export const useProgressState = (key: SnackbarKey): ProgressState | undefined => {
  const subscribe = useCallback(
    (listener: Listener) => progressStore.subscribe(key, listener),
    [key],
  );
  const getSnapshot = useCallback(() => progressStore.get(key), [key]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};
